import { cn } from "@/lib/utils";
import type { FileRecord } from "@cohesive/api-client";
import { Play } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { getFileRecordIconInfo } from "../../lib/file-icon";
import { Spinner } from "../Spinner/Spinner";

export type MediaIntent = "thumbnail" | "full";

/**
 * Picks the best URL for inline media on cards vs detail panes.
 *
 * - `"thumbnail"` (cards): an `<img>` can only render image/video frames,
 *   so for non-renderable types (PDF, docx, md, …) we can't fall back to
 *   the main `url` and have to use the derived 640px JPG preview. For
 *   renderable types we still prefer the smaller derived preview to keep
 *   grid scrolls cheap.
 *
 * - `"full"` (detail pane): the pane wraps the URL in the right element
 *   per type (`<iframe>` for PDF, `<img>` for images, `<video>` for
 *   video, …) so the main `url` is always the correct choice — even for
 *   PDFs / docx, which the browser renders natively in an iframe. Only
 *   fall back to the derived preview when no main URL is available
 *   (e.g. row still in `pending` before the S3 webhook fires).
 */
export function pickFileRecordMediaURL(
  record: Pick<FileRecord, "preview_urls" | "url" | "content_type">,
  intent: MediaIntent,
  { previewSize = "640" }: { previewSize?: string } = {},
): string | undefined {
  if (intent === "full") {
    return record.url ?? record.preview_urls?.[previewSize];
  }

  const ct = record.content_type ?? "";
  const directlyDisplayable =
    ct.startsWith("image/") || ct.startsWith("video/");

  if (!directlyDisplayable) {
    return record.preview_urls?.[previewSize];
  }
  return record.preview_urls?.[previewSize] ?? record.url;
}

// ────────────────────────────────────────────────────────────────────────────
// Content-type predicates
// ────────────────────────────────────────────────────────────────────────────

/**
 * Static MIME types we're willing to inline-preview with an `<img>` tag.
 *
 * Excluded on purpose:
 *   - `image/svg+xml` — arbitrary XSS surface for untrusted uploads
 *   - `application/pdf` — heavy decode, wants the detail pane
 *
 * Not listed because they get their own play-on-hover treatment:
 *   - `video/*`   — `<video>`-based preview via `isPlayableVideo`
 *   - `image/gif` — still poster + play affordance, animates on hover, via
 *     `isAnimatedImage` / the `"gif"` branch of `FileRecordThumbnail`
 */
const PREVIEWABLE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

export function isPreviewableImage(contentType: string): boolean {
  return PREVIEWABLE_IMAGE_TYPES.has(contentType);
}

/**
 * Animated image types shown like the video card: a still poster (the derived
 * `preview_urls` JPG) with a play badge, swapping to the animated original on
 * hover. Keeps motion off until the user signals intent, instead of autoplaying
 * every GIF in a grid.
 */
const ANIMATED_IMAGE_TYPES = new Set(["image/gif"]);

export function isAnimatedImage(contentType: string): boolean {
  return ANIMATED_IMAGE_TYPES.has(contentType);
}

const PREVIEWABLE_VIDEO_TYPES = new Set([
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
]);

export function isPlayableVideo(contentType: string): boolean {
  return PREVIEWABLE_VIDEO_TYPES.has(contentType);
}

/** Pathname-only key so presigned query-param rotations don't reload media. */
function getMediaUrlPathname(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// FileRecordThumbnail — low-level <img> / <video> / icon renderer
// ────────────────────────────────────────────────────────────────────────────

export interface FileRecordThumbnailProps {
  contentType: string;
  previewUrl?: string;
  /** Fired when the primary video frame is ready to display (canvas handoff). */
  onVideoDisplayReady?: () => void;
  /**
   * Hint for how to render `previewUrl`:
   *   - `"image"` — force `<img>` (derived previews are always JPGs, even
   *     when the source is a text/PDF/docx).
   *   - `"video"` — force `<video>` (hover-to-play).
   *   - `"gif"` — animated image: show `posterUrl` (still) with a play badge,
   *     swap to the animated `previewUrl` on hover.
   *   - `"auto"` / undefined — infer from `contentType`: an MP4 file's main
   *     URL plays as video, an image's main URL renders as `<img>`, anything
   *     else falls back to the icon.
   */
  previewKind?: "image" | "video" | "gif" | "auto";
  /**
   * Static frame shown behind motion media: until `<video>` has a frame, or as
   * the resting poster for a `"gif"` until hover. Typically `preview_urls["640"]`.
   */
  posterUrl?: string;
  alt?: string;
  originType?: string | null;
  iconClassName?: string;
  className?: string;
}

export function FileRecordThumbnail({
  contentType,
  previewUrl,
  previewKind = "auto",
  posterUrl,
  alt,
  originType,
  iconClassName,
  className,
  onVideoDisplayReady,
}: FileRecordThumbnailProps) {
  const [mediaError, setMediaError] = useState(false);
  const [videoDisplaySrc, setVideoDisplaySrc] = useState(previewUrl);
  const [videoReady, setVideoReady] = useState(false);
  const [holdVideoSrc, setHoldVideoSrc] = useState<string | null>(null);
  // GIF: only swap in the (potentially heavy) animated original once the user
  // hovers, so resting cards never download every GIF on the canvas/grid.
  const [gifHovered, setGifHovered] = useState(false);
  const videoPathRef = useRef<string | null>(getMediaUrlPathname(previewUrl));
  const videoReadyRef = useRef(false);
  const videoDisplaySrcRef = useRef<string | undefined>(previewUrl);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { icon: Icon, color: iconColor } = getFileRecordIconInfo(
    contentType ?? "",
    originType,
  );

  videoReadyRef.current = videoReady;
  videoDisplaySrcRef.current = videoDisplaySrc;

  // Hold the last ready frame while a new video URL buffers (blob → server).
  useLayoutEffect(() => {
    setMediaError(false);
    setGifHovered(false);
    if (!previewUrl) {
      videoPathRef.current = null;
      setVideoDisplaySrc(undefined);
      setVideoReady(false);
      setHoldVideoSrc(null);
      return;
    }
    const nextPath = getMediaUrlPathname(previewUrl);
    // Presign refresh: same object, new signature — keep the current src so the
    // element doesn't blank out while metadata reloads.
    if (nextPath && nextPath === videoPathRef.current) {
      return;
    }
    if (videoReadyRef.current && videoDisplaySrcRef.current) {
      setHoldVideoSrc(videoDisplaySrcRef.current);
    }
    videoPathRef.current = nextPath;
    setVideoDisplaySrc(previewUrl);
    setVideoReady(false);
  }, [previewUrl]);

  const resolvedKind: "image" | "video" | "gif" | "none" =
    previewKind === "image"
      ? "image"
      : previewKind === "video"
        ? "video"
        : previewKind === "gif"
          ? "gif"
          : isPreviewableImage(contentType)
            ? "image"
            : isAnimatedImage(contentType)
              ? "gif"
              : isPlayableVideo(contentType)
                ? "video"
                : "none";

  const showImage = resolvedKind === "image" && !!previewUrl && !mediaError;
  const showVideo = resolvedKind === "video" && !!previewUrl && !mediaError;
  // GIF renders if we have either a still poster or the animated original.
  const showGif =
    resolvedKind === "gif" && !mediaError && (!!posterUrl || !!previewUrl);
  // With a poster we can rest on the still and only animate on hover; without
  // one there's no frame to freeze, so the animated original shows directly.
  const gifAnimating = showGif && !!previewUrl && (gifHovered || !posterUrl);

  const handleVideoEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
  };

  const handleVideoLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden bg-card flex items-center justify-center",
        className,
      )}
      onMouseEnter={
        showVideo
          ? handleVideoEnter
          : showGif
            ? () => setGifHovered(true)
            : undefined
      }
      onMouseLeave={
        showVideo
          ? handleVideoLeave
          : showGif
            ? () => setGifHovered(false)
            : undefined
      }
    >
      {showImage && (
        // draggable=false (all previews here): a native <img> drag
        // advertises "Files" and lights up upload drop zones; cards drag
        // via dnd-kit instead.
        <img
          src={previewUrl}
          alt={alt ?? ""}
          draggable={false}
          className={cn("w-full h-full object-cover")}
          onError={() => setMediaError(true)}
        />
      )}
      {showVideo && (
        <>
          {holdVideoSrc && !videoReady && (
            <video
              src={holdVideoSrc}
              muted
              playsInline
              preload="auto"
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {posterUrl && !videoReady && !holdVideoSrc && (
            <img
              src={posterUrl}
              alt=""
              aria-hidden
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <video
            ref={videoRef}
            src={videoDisplaySrc}
            muted
            playsInline
            preload="auto"
            className={cn(
              "w-full h-full object-cover transition-opacity duration-200",
              !videoReady && "opacity-0",
            )}
            onLoadedData={() => {
              setVideoReady(true);
              setHoldVideoSrc(null);
              onVideoDisplayReady?.();
            }}
            onError={() => {
              if (previewUrl && previewUrl !== videoDisplaySrc) {
                setVideoDisplaySrc(previewUrl);
                setVideoReady(false);
                return;
              }
              setMediaError(true);
              setHoldVideoSrc(null);
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm ring-1 ring-white/20 opacity-90 group-hover:opacity-0 transition-opacity">
              <Play className="h-5 w-5 text-white fill-white translate-x-px" />
            </div>
          </div>
        </>
      )}
      {showGif && (
        <>
          {posterUrl && (
            <img
              src={posterUrl}
              alt={alt ?? ""}
              aria-hidden={gifAnimating}
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {gifAnimating && (
            <img
              src={previewUrl}
              alt={alt ?? ""}
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setMediaError(true)}
            />
          )}
          {posterUrl && !gifAnimating && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm ring-1 ring-white/20">
                <Play className="h-5 w-5 text-white fill-white translate-x-px" />
              </div>
            </div>
          )}
        </>
      )}
      {!showImage && !showVideo && !showGif && (
        <div className="flex flex-col items-center justify-center gap-1.5">
          <div
            className="flex items-center justify-center rounded-xl bg-neutral-50 dark:bg-neutral-200"
            style={{
              width: "clamp(36px, 28cqw, 72px)",
              height: "clamp(36px, 28cqw, 72px)",
            }}
          >
            <Icon
              className={cn(iconColor, iconClassName)}
              style={{
                width: "clamp(18px, 14cqw, 36px)",
                height: "clamp(18px, 14cqw, 36px)",
                strokeWidth: 1.5,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FileRecordPreview — record-aware thumbnail (derivation + spinner + thumbnail)
// ────────────────────────────────────────────────────────────────────────────

export interface FileRecordPreviewProps {
  contentType?: string;
  originType?: string | null;
  url?: string;
  previewUrls?: Record<string, string> | null;
  /** `pending` / `processing` drives the mid-processing spinner. */
  status?: string;
  alt?: string;
  /**
   * `thumbnail` — grid cards prefer the CloudFront `preview_urls["640"]`.
   * `full` — canvas tiles / large previews use the main object `url`.
   */
  mediaIntent?: MediaIntent;
  /**
   * Canvas spatial nodes pass `embedded` so the mid-processing spinner is
   * suppressed — the host skeleton owns that loading state.
   */
  embedded?: boolean;
  /** Applied to the rendered spinner well or `FileRecordThumbnail`. */
  className?: string;
  iconClassName?: string;
  spinnerSize?: "sm" | "lg";
  /** When true, the spinner well shows a "Waiting for preview…" label. */
  spinnerLabel?: boolean;
  onVideoDisplayReady?: () => void;
}

/**
 * Resolves the best inline media URL for a file record and renders either a
 * mid-processing spinner or the appropriate `<img>` / `<video>` / icon via
 * `FileRecordThumbnail`. This is the shared "thumbnail logic" consumed by
 * both `FileRecordCard` (entity surfaces) and the workspace `WorkspaceFileCard`.
 */
export function FileRecordPreview({
  contentType,
  originType,
  url,
  previewUrls,
  status,
  alt,
  mediaIntent = "thumbnail",
  embedded = false,
  className,
  iconClassName,
  spinnerSize = "lg",
  spinnerLabel = false,
  onVideoDisplayReady,
}: FileRecordPreviewProps) {
  const ct = contentType ?? "";
  const directlyDisplayable =
    ct.startsWith("image/") || ct.startsWith("video/");
  const previewJpg = previewUrls?.["640"];
  const isGif = isAnimatedImage(ct);

  const shouldUseDerivedPreviewForCard =
    previewJpg &&
    (!directlyDisplayable ||
      (mediaIntent === "full" &&
        ct.startsWith("image/") &&
        !isPreviewableImage(ct)));

  // GIFs render like the video card: the animated original (`url`) is the
  // play target and the derived still JPG is the resting poster. Non-renderable
  // content (PDF, docx, md, html, …) can't go into `<img>`. Canvas spatial
  // nodes pass `mediaIntent="full"` to avoid low-res previews for normal
  // photos, but unsafe / non-previewable image originals (notably SVG) still
  // render through the derived JPG, matching workspace.
  const mediaSrc = isGif
    ? (url ?? previewJpg)
    : shouldUseDerivedPreviewForCard
      ? previewJpg
      : directlyDisplayable
        ? pickFileRecordMediaURL(
            { preview_urls: previewUrls ?? undefined, url, content_type: ct },
            mediaIntent,
          )
        : previewJpg;

  // Force `<img>` rendering when we resolved to the derived 640px JPG
  // (always a JPEG regardless of the underlying content type) so a PDF /
  // markdown / docx with a generated preview shows the page thumbnail
  // instead of falling through to the file type icon.
  const isDerivedPreview =
    !isGif && !!mediaSrc && (!directlyDisplayable || mediaSrc === previewJpg);
  const thumbnailKind: "image" | "video" | "gif" | "auto" = isGif
    ? "gif"
    : isDerivedPreview
      ? "image"
      : "auto";
  const videoPosterUrl = isGif
    ? previewJpg
    : isPlayableVideo(ct) && !isDerivedPreview
      ? previewUrls?.["640"]
      : undefined;

  const isAwaitingObject = status === "pending" || status === "processing";
  // No mid-processing overlay when the card already has something to show,
  // or when `embedded` (canvas context): the canvas skeleton owns the loading
  // state, so the spinner would only flash between skeleton and card.
  const showSpinnerOnly = isAwaitingObject && !mediaSrc && !embedded;

  if (showSpinnerOnly) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-card text-lc-warning dark:text-lc-warning",
          spinnerLabel && "flex-col gap-2",
          className,
        )}
      >
        <Spinner size={spinnerSize} />
        {spinnerLabel && (
          <p className="text-xs text-muted-foreground px-4 text-center">
            Waiting for preview…
          </p>
        )}
      </div>
    );
  }

  return (
    <FileRecordThumbnail
      contentType={contentType || "application/octet-stream"}
      originType={originType}
      previewUrl={mediaSrc}
      previewKind={thumbnailKind}
      posterUrl={videoPosterUrl}
      alt={alt}
      className={className}
      iconClassName={iconClassName}
      onVideoDisplayReady={onVideoDisplayReady}
    />
  );
}
